import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, Article, Waveform, Cube, Certificate } from "@phosphor-icons/react";
import { Section, PageHero, EyebrowLabel, SectionHeading, CalloutCard } from "@/components/marketing/MarketingUI";
import useSEO from "@/lib/useSEO";

const POSTS = [
  {
    slug: "the-reproducible-resource-statement",
    icon: Certificate,
    title: "The Reproducible Resource Statement",
    tag: "Point of view · 12 min read",
    author: "SRES Editorial",
    date: "12 Feb 2026",
    excerpt:
      "Why every kriging run, pit shell and CPR PDF you generate should be snapshotted with its inputs — and how SRES makes that discipline the default rather than the exception.",
    body: [
      "In every audit conversation we have with Competent Persons, the same worry surfaces: ‘Can I re-run the numbers that support this signed statement, exactly as they were, two years from now?’ For most teams the honest answer is no — the CSV moved on the drive, the software version was patched, the spreadsheet tab was renamed. The signed statement is defensible today; the workings are not.",
      "SRES treats reproducibility as a first-class engineering promise. Every kriging run, every pit optimisation, every CPR export is snapshotted with the exact inputs that produced it — assay tables, variogram parameters, density map, cut-off strategy — and pinned inside the tenant so any past run can be re-downloaded byte-for-byte, months or years later.",
      "The consequence is subtle but powerful. Because reproducibility is cheap, teams stop treating a signed resource statement as a fragile artefact. They iterate more, they domain more thoroughly, they explore more cut-off sweeps — because there is no operational cost to producing a defensible new snapshot. Rigour compounds.",
    ],
  },
  {
    slug: "ordinary-kriging-without-the-pain",
    icon: Cube,
    title: "Ordinary Kriging Without the Pain",
    tag: "Tutorial · 18 min read",
    author: "SRES Geoscience",
    date: "27 Jan 2026",
    excerpt:
      "A practical walkthrough of variogram fitting, composite lengths and per-domain estimation — designed to demystify OK for teams new to formal geostatistics.",
    body: [
      "For teams graduating from inverse-distance estimation, Ordinary Kriging often feels like a step off a cliff. The literature is dense, the parameters are unfamiliar, and the software historically buried the workings behind opaque dialog boxes. In practice, defensible OK is much less mysterious than it looks.",
      "The heart of the discipline is the variogram — a summary of how similar two samples are as a function of the distance between them. SRES computes an experimental variogram from your assays, then fits a spherical, exponential or Gaussian model with a small set of geologically-interpretable parameters: nugget, sill and range. Every fit is a chart you can eyeball, share, and defend.",
      "Composite lengths, domaining and search neighbourhoods complete the picture. SRES exposes each choice as an explicit knob — not a hidden default — and records what you picked into the CPR narrative that ships with your final PDF. There is nowhere for a bad assumption to hide.",
    ],
  },
  {
    slug: "monte-carlo-npv-done-right",
    icon: Waveform,
    title: "Monte-Carlo NPV, Done Right",
    tag: "Playbook · 9 min read",
    author: "SRES Editorial",
    date: "14 Jan 2026",
    excerpt:
      "Triangular distributions, P10/P50/P90 disclosures and the discipline of communicating economic risk to boards — with SRES defaults you can adopt today.",
    body: [
      "Deterministic NPV numbers are neat but dangerous. They anchor the board on a single figure that ignores the ranges of commodity price, mining cost, processing recovery and dilution that drive real project economics. Monte-Carlo NPV — with even a modest 5,000-trial run — restores the honest picture.",
      "SRES defaults to triangular distributions because they are transparent to non-statistical readers: a minimum, a most-likely and a maximum. Every simulation captures the full distribution, the P10/P50/P90 percentiles, and a histogram you can drop straight into the CPR.",
      "The trick is not the maths — the trick is the discipline of disclosure. SRES pins the seed, the trial count and the input distributions into the report so a reviewer can replay the simulation, byte-for-byte, and verify the numbers were not cherry-picked.",
    ],
  },
  {
    slug: "from-csv-to-signed-cpr-in-one-afternoon",
    icon: Article,
    title: "From CSV to Signed CPR in One Afternoon",
    tag: "Case study · 15 min read",
    author: "SRES Delivery",
    date: "22 Dec 2025",
    excerpt:
      "A senior Competent Person takes an iron-ore project from raw drillhole database to a JORC-aligned CPR PDF in a single afternoon working session. Here is exactly how.",
    body: [
      "The client — a mid-cap iron ore developer — arrived with 112 drillholes, four CSVs (Collar, Survey, Assay, Geology) and a deadline: a JORC-aligned CPR PDF for the following morning's board. Their previous provider needed three weeks and a two-week onboarding.",
      "In SRES, the ingest step is measured in minutes. Column alias auto-mapping picked up BHID, easting, from/to and the Fe grade column without manual configuration. Validation flagged three geometry overlaps and a single out-of-hole assay; all resolved before lunch.",
      "By late afternoon the block model was kriged, the pit shell optimised at three cut-off scenarios, and the CPR PDF assembled with narrative, figures and the CP's signature block. The signed statement went to the board with the reproducible snapshot pinned to the workspace — and the client kept it.",
    ],
  },
];

export default function BlogPage() {
  useSEO({
    title: "Blog · Insights from working resource geologists",
    description: "Long-form writing on kriging, reproducibility, Competent Person reporting and modern resource estimation practice — from the SRES geoscience team.",
    keywords: "resource estimation blog, kriging tutorial, Competent Person Report, JORC, NI 43-101, SAMREC, block modelling",
    path: "/blog",
  });

  return (
    <div data-testid="blog-page">
      <PageHero
        eyebrow="Blog & insights"
        title="Practical writing on modern resource practice."
        description="Long-form articles from working resource geologists, mine planners and Competent Persons — no filler, no vendor puffery."
        testid="blog-hero"
      />

      <Section className="py-20 md:py-24" testid="blog-list">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {POSTS.map((p, i) => (
            <article
              key={p.slug}
              className="p-8 rounded-2xl border border-slate-200 bg-white hover:border-brand-blue/50 hover:shadow-[0_16px_50px_-20px_rgba(30,111,255,0.35)] transition-all flex flex-col"
              data-testid={`blog-post-${p.slug}`}
            >
              <div className="w-12 h-12 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                <p.icon size={22} weight="bold" />
              </div>
              <div className="mt-5 text-[11px] uppercase tracking-[0.22em] text-brand-blue font-mono">{p.tag}</div>
              <h2 className="mt-2 font-heading font-bold text-slate-900 text-xl leading-snug">{p.title}</h2>
              <p className="mt-3 text-slate-600 text-[14px] leading-relaxed flex-1">{p.excerpt}</p>
              <div className="mt-6 pt-6 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <span className="inline-flex items-center gap-1"><User size={12} /> {p.author}</span>
                <span className="inline-flex items-center gap-1"><Calendar size={12} /> {p.date}</span>
              </div>
              <a
                href={`#${p.slug}`}
                onClick={(e) => { e.preventDefault(); document.getElementById(p.slug)?.scrollIntoView({ behavior: "smooth" }); }}
                className="mt-5 inline-flex items-center gap-2 text-brand-blue text-[13px] font-semibold hover:underline"
              >
                Read the article <ArrowRight size={13} weight="bold" />
              </a>
            </article>
          ))}
        </div>
      </Section>

      <Section className="py-16 md:py-24 bg-[#F7F8FB]" testid="blog-articles">
        <div className="space-y-24">
          {POSTS.map((p) => (
            <article key={p.slug} id={p.slug} className="max-w-3xl mx-auto scroll-mt-24">
              <EyebrowLabel className="mb-5">{p.tag}</EyebrowLabel>
              <h2 className="font-heading font-black text-slate-900 text-3xl md:text-4xl tracking-tight leading-tight">{p.title}</h2>
              <div className="mt-4 flex items-center gap-4 text-[12px] text-slate-500">
                <span className="inline-flex items-center gap-1"><User size={12} /> {p.author}</span>
                <span className="inline-flex items-center gap-1"><Calendar size={12} /> {p.date}</span>
              </div>
              <div className="mt-8 space-y-5 text-slate-700 text-[16px] leading-[1.75]">
                {p.body.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              <div className="mt-10 pt-6 border-t border-slate-200 flex items-center gap-3 text-[13px] text-slate-500">
                Enjoyed this? Share it with your resource team.
                <Link to="/contact" className="text-brand-blue font-semibold hover:underline">Talk to us →</Link>
              </div>
            </article>
          ))}
        </div>
      </Section>

      <Section className="py-16 md:py-24" testid="blog-cta">
        <CalloutCard
          eyebrow="Want more like this?"
          title="Subscribe to SRES Field Notes — one email a month, only from working geologists."
          description="No spam, no sales sequences. Just the writing our own team wishes had existed when we started."
          cta="Talk to us"
          to="/contact"
          testid="blog-cta-callout"
        />
      </Section>
    </div>
  );
}
